import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  CheckCircle,
  XCircle,
  Shield,
  User,
  MapPin,
  Smartphone,
  Cpu,
} from "lucide-react";

const API = process.env.REACT_APP_API_URL;

const VerificarReportePage = () => {
  const { tokenPublico } = useParams();
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verificar = async () => {
      try {
        const res = await axios.get(`${API}/reportes/verify/${tokenPublico}`);
        setResultado(res.data);
      } catch (err) {
        setResultado({
          valido: false,
          mensaje: "❌ Token no válido o error en la verificación",
        });
      } finally {
        setLoading(false);
      }
    };
    verificar();
  }, [tokenPublico]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen text-gray-600">
        <Shield className="w-10 h-10 mb-3 animate-spin text-blue-600" />
        <p>Verificando autenticidad del reporte...</p>
      </div>
    );
  }

  const gestion = resultado?.gestion;
  const firmante = resultado?.firmante;

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 px-6 py-10">
      <div className="bg-white shadow-xl rounded-2xl p-8 max-w-2xl w-full text-center border border-gray-100">
        <img src="/logo.png" alt="Previonix" className="h-12 mx-auto mb-6" />

        {resultado?.valido ? (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-green-600">
              Reporte verificado correctamente
            </h2>
            <p className="text-gray-600 mt-2">
              El documento es auténtico y no ha sido modificado.
            </p>

            {/* Datos principales */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-5 mt-6 text-left text-sm space-y-2">
              <p>
                <b>ID de gestión:</b> {resultado.gestionId}
              </p>
              <p>
                <b>Token público:</b> {tokenPublico}
              </p>
              <p>
                <b>Estado:</b> {gestion?.estado}
              </p>
              <p>
                <b>Fecha de cierre:</b>{" "}
                {new Date(gestion?.fechaCierre).toLocaleString("es-CO")}
              </p>
            </div>

            {/* Datos del cronograma / equipo */}
            {gestion?.cronograma && (
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 mt-4 text-left text-sm">
                <h3 className="font-semibold mb-2 text-gray-700 flex items-center gap-2">
                  <Cpu size={16} /> Equipo asociado
                </h3>
                {gestion.cronograma.equipo ? (
                  <>
                    <p>
                      <b>Equipo:</b> {gestion.cronograma.equipo.categoria?.name}
                    </p>
                    <p>
                      <b>Código:</b> {gestion.cronograma.equipo.codigo}
                    </p>
                    <p>
                      <b>Marca:</b> {gestion.cronograma.equipo.marca?.name}
                    </p>
                    <p>
                      <b>Modelo:</b> {gestion.cronograma.equipo.modelo?.name}
                    </p>
                    <p>
                      <b>Serial:</b> {gestion.cronograma.equipo.serial}
                    </p>
                  </>
                ) : (
                  <p>No se encontró información del equipo.</p>
                )}
              </div>
            )}

            {/* Firmante */}
            {firmante && (
              <div className="bg-white border border-gray-200 rounded-xl p-5 mt-6 text-left text-sm">
                <h3 className="font-semibold mb-3 text-gray-700 flex items-center gap-2">
                  <User size={16} /> Firmante
                </h3>
                <p>
                  <b>Tipo de servicio:</b> {firmante.tipo}
                </p>
                <p>
                  <b>Nombre:</b> {firmante.nombre}
                </p>
                {firmante.rol && (
                  <p>
                    <b>Rol:</b> {firmante.rol}
                  </p>
                )}
                <p>
                  <b>Fecha de firma:</b> {firmante.fechaFirma}
                </p>
                <p className="flex items-center gap-2 mt-2">
                  <MapPin size={14} className="text-gray-500" />{" "}
                  <span>
                    <b>Ubicación:</b> {firmante.ubicacion || "No registrada"}
                  </span>
                </p>
                <p className="flex items-center gap-2 mt-2">
                  <Smartphone size={14} className="text-gray-500" />{" "}
                  <span>
                    <b>Dispositivo:</b> {firmante.dispositivo || "Desconocido"}
                  </span>
                </p>
                <p>
                  <b>IP:</b> {firmante.ip || "No disponible"}
                </p>

                {firmante.firmaDigital && (
                  <div className="mt-4 text-center">
                    <p className="text-xs text-gray-500 mb-2">
                      Firma registrada:
                    </p>
                    <img
                      src={firmante.firmaDigital}
                      alt="Firma del autor"
                      className="mx-auto h-20 object-contain"
                    />
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-red-600">
              Reporte no válido
            </h2>
            <p className="text-gray-600 mt-2">
              {resultado?.mensaje ||
                "El token no existe o el documento fue alterado."}
            </p>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4 text-left text-sm">
              <p>
                <b>Token escaneado:</b> {tokenPublico}
              </p>
            </div>
          </>
        )}
      </div>

      <footer className="mt-6 text-xs text-gray-500">
        © {new Date().getFullYear()} Previonix®
      </footer>
    </div>
  );
};

export default VerificarReportePage;
